import { formatNumber } from '@shared/format';

import { copy } from './copy';
import type { SortMode } from './types';

/**
 * The one strip above the table: how the inbox is ordered, and what a selection
 * can be made to do.
 *
 * **One panel, because it is one act.** Ordering the list and acting on rows in
 * it were drawn as two loose rows — an order control floated to the far edge of
 * the heading line, and three unframed buttons pushed against the top of the
 * table with the status line trailing behind them. Nothing tied any of it to the
 * table it governs, and the ragged edges read as three unrelated fragments
 * rather than one control surface. They are now one bordered strip that sits on
 * the table: the view control at the start of the line, the actions at the end
 * of it, and the state of the selection on its own row beneath both.
 *
 * **All three actions stay disabled until a selection exists, and the reason is
 * written at the control** — a disabled control with no written reason is a
 * defect, and a note at the foot of the page is not «at the control». The same
 * line carries the selected count, so one element answers both «why are these
 * off» and «how many did I pick».
 *
 * **No confirmation on delete**, and it is the one place this console's
 * confirm-before-destroy posture does not reach: a notification is a copy of
 * something that already happened, and removing it destroys no subject.
 *
 * The three buttons are secondary and danger, never primary — the top bar's
 * create action is this view's one primary button.
 */
export function InboxToolbar({
  sort,
  onSortChange,
  selectedCount,
  busy,
  onMarkRead,
  onMarkUnread,
  onDelete,
}: {
  sort: SortMode;
  onSortChange: (next: SortMode) => void;
  selectedCount: number;
  busy: boolean;
  onMarkRead: () => void;
  onMarkUnread: () => void;
  onDelete: () => void;
}) {
  const idle = selectedCount === 0;
  const disabled = idle || busy;

  return (
    <div className="mb-4 grid gap-3 rounded-md border border-border bg-surface-raised p-4">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
        {/*
          Order here is a **view**, not a field: reordering the inbox writes to
          no record, so a control that changes it on the spot is the right
          shape.
        */}
        <label className="flex items-center gap-2 text-sm">
          <span className="font-medium">{copy.sort.label}</span>
          <select
            value={sort}
            onChange={(event) => onSortChange(event.currentTarget.value as SortMode)}
            className="hit-target max-w-[14rem] cursor-pointer rounded-sm border border-border-strong bg-surface px-3 text-sm text-ink hover:border-ink-muted focus-visible:border-accent"
          >
            {SORT_MODES.map((mode) => (
              <option key={mode} value={mode}>
                {copy.sort.options[mode]}
              </option>
            ))}
          </select>
        </label>

        <div className="flex flex-wrap items-center gap-2">
          <BulkButton disabled={disabled} onClick={onMarkRead}>
            {copy.bulk.markRead}
          </BulkButton>
          <BulkButton disabled={disabled} onClick={onMarkUnread}>
            {copy.bulk.markUnread}
          </BulkButton>
          <BulkButton disabled={disabled} onClick={onDelete} tone="danger">
            {copy.bulk.delete}
          </BulkButton>
        </div>
      </div>

      <p className="mb-0 max-w-measure text-xs text-ink-muted">
        {idle ? (
          copy.bulk.idle
        ) : (
          <>
            {copy.bulk.selectedPrefix} <bdi>{formatNumber(selectedCount)}</bdi> {copy.bulk.selectedSuffix}
          </>
        )}
      </p>
    </div>
  );
}

/** The three orders the service and this surface between them can produce. */
const SORT_MODES: readonly SortMode[] = ['date-desc', 'date-asc', 'unread'];

function BulkButton({
  disabled,
  tone = 'secondary',
  onClick,
  children,
}: {
  disabled: boolean;
  tone?: 'secondary' | 'danger';
  onClick: () => void;
  children: string;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={
        tone === 'danger'
          ? 'hit-target rounded-sm border border-danger px-3 text-xs font-semibold text-danger hover:bg-danger-soft disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent'
          : 'hit-target rounded-sm border border-border-strong bg-surface px-3 text-xs font-semibold text-ink hover:bg-surface-overlay disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-surface'
      }
    >
      {children}
    </button>
  );
}
